'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface Mentor {
  name: string;
  title: string;
  description: string;
  image: string;
  alt: string;
  gradient: string;
  path: string;
  objectPosition: string;
}

interface MentorsSectionProps {
  className?: string;
}

const MentorsSection = ({ className = '' }: MentorsSectionProps) => {
  const router = useRouter();
  const { isAuthenticated } = useAuth();

  const mentors: Mentor[] = [
    {
      name: 'AI Coach',
      title: 'Coach de Startups y Negocios',
      description: 'Validación empresarial, orientación estratégica y soporte en decisiones con mentalidad de fundador.',
      image: '/assets/images/AI_coach-1770284266834.png',
      alt: 'AI Coach - Coach general de startups y negocios para validación y orientación estratégica',
      gradient: 'from-orange-500 to-amber-600',
      path: '/ai-business-coach',
      objectPosition: 'center 38%',
    },
    {
      name: 'AI-de',
      title: 'Studio de Validación de Ideas',
      description: 'Organiza y enriquece tus ideas, obtén un diagnóstico de factibilidad en 12 dimensiones, valida con ciclos Lean y envía lo que funciona al AI Coach.',
      image: '/assets/images/aide_ideamaker-1770282880344.png',
      alt: 'AI-de - Amplificador de ideas para mejorar y estructurar conceptos empresariales',
      gradient: 'from-green-500 to-emerald-600',
      path: '/ai-idea-coach',
      objectPosition: 'center 24%',
    },
    {
      name: 'Gen-IA',
      title: 'Coach de Lluvia de Ideas',
      description: 'Genera ideas innovadoras, expande conceptos y rompe bloqueos mentales en la fase inicial de ideación.',
      image: '/assets/images/genia_lluvia_ideas-1770283270530.png',
      alt: 'Gen-IA - Coach de lluvia de ideas para generación y expansión de conceptos creativos',
      gradient: 'from-purple-500 to-pink-600',
      path: '/brainstorm-coach',
      objectPosition: 'center 32%',
    },
    {
      name: 'SocrIAtes',
      title: 'Coach de Pensamiento Paralelo',
      description: 'Desafía suposiciones, replantea problemas desde nuevos ángulos y activa el pensamiento disruptivo.',
      image: '/assets/images/socriates_pensamiento_paralelo-1770284247284.png',
      alt: 'SocrIAtes - Coach de pensamiento paralelo para análisis lateral y replanteamiento estratégico',
      gradient: 'from-blue-500 to-indigo-600',
      path: '/obx',
      objectPosition: 'center 30%',
    },
  ];

  const handleMentorClick = (path: string) => {
    if (isAuthenticated) {
      router.push(path);
      return;
    }

    const redirectParam = encodeURIComponent(path);
    router.push(`/register?redirect=${redirectParam}`);
  };

  return (
    <section className={`bg-gradient-to-b from-transparent to-white/30 py-20 lg:py-32 ${className}`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="section-eyebrow mb-6">
            <Icon name="UserGroupIcon" size={20} className="text-primary" />
            <span>Tu Equipo de IA</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6">
            Conoce a tus{' '}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'var(--gradient-app)' }}>
              Mentores de IA
            </span>
          </h2>

          <p className="text-xl text-gray-600 leading-relaxed">
            Cada mentor está especializado en una fase específica de tu proceso emprendedor, brindándote el apoyo exacto que necesitas en cada momento.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {mentors.map((mentor, index) => (
            <div
              key={index}
              className="app-card group rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-elevation-xl"
            >
              <div className="relative mb-6">
                <div className={`absolute inset-0 bg-gradient-to-br ${mentor.gradient} rounded-2xl opacity-20 blur-xl group-hover:opacity-30 transition-opacity duration-300`}></div>
                <div className="relative w-full h-48 sm:h-52 rounded-2xl overflow-hidden shadow-md">
                  <AppImage
                    src={mentor.image}
                    alt={mentor.alt}
                    fill
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                    style={{ objectPosition: mentor.objectPosition }}
                  />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                {mentor.name}
              </h3>

              <p className="mb-4 text-center text-sm font-semibold text-primary">
                {mentor.title}
              </p>

              <p className="text-gray-600 leading-relaxed text-center">
                {mentor.description}
              </p>

              <button
                type="button"
                onClick={() => handleMentorClick(mentor.path)}
                className="btn-primary-app mt-6 w-full px-5 py-3 hover:scale-[1.02]"
              >
                Ir con {mentor.name}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-lg text-gray-600 mb-6">
            Con cualquier suscripción, desbloqueas a todo tu equipo de mentores IA para acompañarte desde la idea hasta la ejecución.
          </p>
        </div>
      </div>
    </section>
  );
};

export default MentorsSection;
